import React, { useMemo, useState } from 'react';
import { ChevronDown, TrendingUp, TrendingDown, Activity, MessageSquare, Shield, Heart, Sparkles } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Scan } from '../../types/scan';
interface CommunicationStyleWidgetProps {
  scans: Scan[];
}
/** Qualitative band instead of a precise-looking psychological score. */
function getSignalLabel(value: number): string {
  if (value >= 70) return 'High signal';
  if (value >= 45) return 'Moderate signal';
  return 'Low signal';
}
export function CommunicationStyleWidget({
  scans
}: CommunicationStyleWidgetProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [hoveredPoint, setHoveredPoint] = useState<{
    traitIndex: number;
    pointIndex: number;
    value: number;
    x: number;
    y: number;
  } | null>(null);
  // Helper function to safely get patterns array
  const getPatterns = (scan: Scan): any[] => {
    if (!scan.patterns) return [];
    if (Array.isArray(scan.patterns)) return scan.patterns;
    // If patterns is a string, try to parse it
    if (typeof scan.patterns === 'string') {
      try {
        const parsed = JSON.parse(scan.patterns);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    }
    // If patterns is an object, wrap it in an array
    if (typeof scan.patterns === 'object') {
      return [scan.patterns];
    }
    return [];
  };
  // Analyze real scan data to derive communication metrics
  const communicationData = useMemo(() => {
    const completedScans = scans.filter(s => s.status === 'completed' && s.patterns);
    if (completedScans.length === 0) {
      return null;
    }
    // Map pattern types to communication traits
    const calculateTraitScore = (patternTypes: string[]) => {
      const assertivenessPatterns = ['passive-aggressive', 'indirect-communication', 'avoidance'];
      const empathyPatterns = ['dismissive', 'invalidation', 'lack-of-empathy', 'gaslighting'];
      const boundaryPatterns = ['manipulation', 'guilt-tripping', 'boundary-violation', 'control'];
      const assertiveness = 100 - patternTypes.filter(p => assertivenessPatterns.some(ap => p.toLowerCase().includes(ap))).length * 15;
      const empathy = 100 - patternTypes.filter(p => empathyPatterns.some(ep => p.toLowerCase().includes(ep))).length * 15;
      const boundarySetting = 100 - patternTypes.filter(p => boundaryPatterns.some(bp => p.toLowerCase().includes(bp))).length * 15;
      return {
        assertiveness: Math.max(20, Math.min(100, assertiveness)),
        empathy: Math.max(20, Math.min(100, empathy)),
        boundarySetting: Math.max(20, Math.min(100, boundarySetting))
      };
    };
    // Get last 5 scans for trend analysis (chronological: oldest → newest)
    const recentScans = completedScans.slice(0, 5).reverse();
    const assertivenessValues: number[] = [];
    const empathyValues: number[] = [];
    const boundaryValues: number[] = [];
    recentScans.forEach(scan => {
      const patterns = getPatterns(scan);
      const patternTypes = patterns.map(p => p.type || '');
      const scores = calculateTraitScore(patternTypes);
      assertivenessValues.push(scores.assertiveness);
      empathyValues.push(scores.empathy);
      boundaryValues.push(scores.boundarySetting);
    });
    // No synthetic padding — chart renders only actual user scan data points.
    // This avoids a misleading flat line at a single repeated value when the
    // user has fewer than 5 scans (which previously produced "always 100" looking charts).
    const currentAssertiveness = assertivenessValues[assertivenessValues.length - 1];
    const currentEmpathy = empathyValues[empathyValues.length - 1];
    const currentBoundary = boundaryValues[boundaryValues.length - 1];
    // Determine persona based on current scores
    const avgScore = (currentAssertiveness + currentEmpathy + currentBoundary) / 3;
    let persona = 'Balanced Communicator';
    if (currentAssertiveness >= 70 && currentBoundary >= 70) {
      persona = 'Assertive Communicator';
    } else if (currentEmpathy >= 75) {
      persona = 'Empathetic Communicator';
    } else if (currentBoundary >= 70) {
      persona = 'Boundary-Conscious Communicator';
    } else if (avgScore < 50) {
      persona = 'Developing Communicator';
    }
    // Calculate changes from previous scan
    const changes: any[] = [];
    if (recentScans.length >= 2) {
      const prevAssertiveness = assertivenessValues[assertivenessValues.length - 2];
      const prevEmpathy = empathyValues[empathyValues.length - 2];
      const prevBoundary = boundaryValues[boundaryValues.length - 2];
      const assertivenessChange = currentAssertiveness - prevAssertiveness;
      const empathyChange = currentEmpathy - prevEmpathy;
      const boundaryChange = currentBoundary - prevBoundary;
      if (Math.abs(assertivenessChange) >= 5) {
        changes.push({
          trait: 'Assertiveness',
          change: Math.abs(Math.round(assertivenessChange)),
          direction: assertivenessChange > 0 ? 'up' : 'down',
          example: assertivenessChange > 0 ? 'More direct communication patterns detected in recent conversations' : 'Less assertive language patterns observed',
          scanRef: `#${completedScans[0].id.slice(0, 6)}`
        });
      }
      if (Math.abs(empathyChange) >= 5) {
        changes.push({
          trait: 'Empathy',
          change: Math.abs(Math.round(empathyChange)),
          direction: empathyChange > 0 ? 'up' : 'down',
          example: empathyChange > 0 ? 'More active listening and validation markers detected' : 'Fewer empathetic response patterns observed',
          scanRef: `#${completedScans[0].id.slice(0, 6)}`
        });
      }
      if (Math.abs(boundaryChange) >= 5) {
        changes.push({
          trait: 'Boundary-Setting',
          change: Math.abs(Math.round(boundaryChange)),
          direction: boundaryChange > 0 ? 'up' : 'down',
          example: boundaryChange > 0 ? 'Clearer boundary-setting language detected in responses' : 'Less defined boundary communication observed',
          scanRef: `#${completedScans[0].id.slice(0, 6)}`
        });
      }
    }
    // Check if persona recently evolved (comparing first and last scan)
    const firstPatterns = getPatterns(recentScans[0]);
    const lastPatterns = getPatterns(recentScans[recentScans.length - 1]);
    const firstScores = calculateTraitScore(firstPatterns.map(p => p.type || ''));
    const lastScores = calculateTraitScore(lastPatterns.map(p => p.type || ''));
    const avgChange = Math.abs((lastScores.assertiveness - firstScores.assertiveness + (lastScores.empathy - firstScores.empathy) + (lastScores.boundarySetting - firstScores.boundarySetting)) / 3);
    const recentlyEvolved = avgChange >= 15;
    return {
      currentPersona: persona,
      confidence: Math.round(avgScore),
      recentlyEvolved,
      traits: [{
        name: 'Assertiveness',
        values: assertivenessValues,
        color: '#DC2626',
        icon: MessageSquare,
        currentValue: Math.round(currentAssertiveness)
      }, {
        name: 'Empathy',
        values: empathyValues,
        color: '#10B981',
        icon: Heart,
        currentValue: Math.round(currentEmpathy)
      }, {
        name: 'Boundary-Setting',
        values: boundaryValues,
        color: '#9333EA',
        icon: Shield,
        currentValue: Math.round(currentBoundary)
      }],
      recentChanges: changes,
      scanCount: completedScans.length
    };
  }, [scans]);
  // If no data available, show empty state
  if (!communicationData) {
    return <div className="relative bg-[#0a0a0a] border border-gray-800 overflow-hidden mb-8" data-id="element-930" data-mp="uqd5hvty7f1c">
        <div className="absolute top-0 left-0 w-4 h-4 border-t-2 border-l-2 border-gray-600" data-id="element-931" data-mp="uo0q9qfkrirz" />
        <div className="absolute top-0 right-0 w-4 h-4 border-t-2 border-r-2 border-gray-600" data-id="element-932" data-mp="qmxepg4k88wk" />
        <div className="absolute bottom-0 left-0 w-4 h-4 border-b-2 border-l-2 border-gray-600" data-id="element-933" data-mp="2wt9j4bq413g" />
        <div className="absolute bottom-0 right-0 w-4 h-4 border-b-2 border-r-2 border-gray-600" data-id="element-934" data-mp="3u3nng2rj101y" />

        <div className="p-8 text-center" data-id="element-935" data-mp="btc22fg1lmzi">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-[#FF0000]/10 border border-[#FF0000] mb-4" data-id="element-936" data-mp="70iu7ipa1yj7">
            <Activity className="w-8 h-8 text-[#FF0000]" data-id="element-937" data-mp="3ff19o5hjsgcu" />
          </div>
          <h3 className="text-lg font-black text-white uppercase tracking-tight mb-2" data-id="element-938" data-mp="3bfsgb1qf6kht">
            Communication Forensics
          </h3>
          <p className="text-gray-400 font-mono text-sm mb-6" data-id="element-939" data-mp="319r8mqi0fyju">
            Initialize first scan to generate behavioral communication profile
          </p>
        </div>
      </div>;
  }
  // Chart dimensions
  const width = 100;
  const height = 40;
  const padding = 2;
  // Helper to scale values to SVG coordinates
  // Guard: when only 1 data point exists, center it horizontally to avoid NaN.
  const getX = (index: number, total: number) => total <= 1 ? width / 2 : index / (total - 1) * (width - padding * 2) + padding;
  const getY = (value: number) => height - value / 100 * (height - padding * 2) - padding;
  // Generate path string for a line
  const getPath = (values: number[]) => {
    return values.map((val, i) => `${i === 0 ? 'M' : 'L'} ${getX(i, values.length)} ${getY(val)}`).join(' ');
  };
  const topChange = communicationData.recentChanges[0];
  return <div className="relative bg-[#0a0a0a] border border-gray-800 overflow-hidden mb-8" data-id="element-940" data-mp="38vf6wppg81rb">
      <div className="absolute top-0 left-0 w-4 h-4 border-t-2 border-l-2 border-gray-600" data-id="element-941" data-mp="3du8iudp37kpd" />
      <div className="absolute top-0 right-0 w-4 h-4 border-t-2 border-r-2 border-gray-600" data-id="element-942" data-mp="3dw2xja9rmgl0" />
      <div className="absolute bottom-0 left-0 w-4 h-4 border-b-2 border-l-2 border-gray-600" data-id="element-943" data-mp="2z3hrf6reqnms" />
      <div className="absolute bottom-0 right-0 w-4 h-4 border-b-2 border-r-2 border-gray-600" data-id="element-944" data-mp="3422hidcy3qxi" />

      {/* PRIMARY: Current Persona - Hero Section */}
      <div className="p-6 space-y-4 border-b border-gray-800" data-id="element-945" data-mp="2v4s52ooe5vr0">
        <div className="flex items-start justify-between gap-4" data-id="element-946" data-mp="2g6hq6r0vyd0o">
          <div className="flex-1 space-y-2" data-id="element-947" data-mp="2g8atpxg7ch4g">
            {/* Label - Tertiary */}
            <div className="flex items-center gap-2" data-id="element-948" data-mp="2l6chgvokp4m9">
              <Activity className="w-4 h-4 text-[#FF0000]" data-id="element-949" data-mp="efcyjuyydcgc" />
              <span className="text-sm font-mono text-gray-500 uppercase tracking-widest leading-tight" data-id="element-950" data-mp="efwdr3x7xmqo">
                Communication Signals
              </span>
            </div>

            <h2 className="text-2xl font-black text-white uppercase tracking-tight leading-tight" data-id="element-951" data-mp="23ws094t9icl1">
              {communicationData.currentPersona.replace('Communicator', 'communication signals')}
            </h2>

            <p className="text-sm font-mono text-gray-400 leading-snug" data-id="element-952" data-mp="28uthdk74o1tv">
              Model estimates based on{' '}
              {communicationData.scanCount === 1 ? 'this conversation' : `your last ${Math.min(communicationData.scanCount, 5)} conversations`}
            </p>
          </div>

          {/* Evolution Badge - Visual Cue */}
          {communicationData.recentlyEvolved && <motion.div initial={{
          scale: 0.9,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#FF0000]/10 border border-[#FF0000] text-[#FF0000] text-xs font-mono uppercase tracking-widest" data-id="element-953" data-mp="2rn4h556hjjnr">
              <Sparkles className="w-3.5 h-3.5" data-id="element-954" data-mp="329xkpl6vseki" />
              <span data-id="element-955" data-mp="36apv4hrhopid">Recently Evolved</span>
            </motion.div>}
        </div>

        {/* Latest Change Preview - Above Fold */}
        {topChange && <div className={`p-4 border space-y-2 ${topChange.direction === 'up' ? 'bg-green-500/5 border-green-500/20' : 'bg-orange-500/5 border-orange-500/20'}`} data-id="element-956" data-mp="3bb4545cni9j1">
            <div className="flex items-center gap-2" data-id="element-957" data-mp="3l6fiaj8ev8oo">
              <span className="text-sm font-bold text-white uppercase tracking-wider leading-tight" data-id="element-958" data-mp="3pyyt58fprpps">
                Latest Detection:
              </span>
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 border text-[10px] font-mono uppercase tracking-widest ${topChange.direction === 'up' ? 'bg-green-500/10 text-green-500 border-green-500/30' : 'bg-orange-500/10 text-orange-500 border-orange-500/30'}`} data-id="element-959" data-mp="3tziqod478vcg">
                {topChange.direction === 'up' ? <TrendingUp className="w-3 h-3" data-id="element-960" data-mp="cxo9rmjpbxuq" /> : <TrendingDown className="w-3 h-3" data-id="element-961" data-mp="7yl5437mvtna" />}
                {topChange.trait}{' '}
                {topChange.direction === 'up' ? 'stronger' : 'weaker'}
              </span>
            </div>
            {/* Description - Secondary (text-base text-gray-600) */}
            <p className="text-sm font-mono text-gray-400 leading-relaxed" data-id="element-962" data-mp="vofhykrydi7j">
              {topChange.example}
            </p>
          </div>}
      </div>

      {/* SECONDARY: Trait Scores Section */}
      <div className="p-6 space-y-4 border-b border-gray-800" data-id="element-963" data-mp="mwdjut5cwe0f">
        <h3 className="text-[10px] font-mono text-gray-500 uppercase tracking-widest leading-tight" data-id="element-964" data-mp="mvvhdes3p3k2">
          Signal Strength
        </h3>

        {/* Trait Cards - 8px Grid Aligned */}
        <div className="grid grid-cols-3 gap-4" data-id="element-965" data-mp="1elqj6ax5alpm">
          {communicationData.traits.map(trait => {
          const Icon = trait.icon;
          return <div key={trait.name} className="text-center space-y-2" data-id="element-966" data-mp="144my5eic0m59">
                <div className="w-12 h-12 mx-auto flex items-center justify-center border" style={{
              '--trait-color': trait.color,
              '--trait-color-15': `${trait.color}15`,
              '--trait-color-50': `${trait.color}50`,
              backgroundColor: 'var(--trait-color-15)',
              borderColor: 'var(--trait-color-50)'
            } as React.CSSProperties} data-id="element-967" data-mp="q4lmd40j3ub0">
                  <Icon className="w-5 h-5" style={{
                color: trait.color
              }} data-id="element-968" data-mp="1d3zz1ise42ed" />
                </div>
                <div className="text-sm font-bold text-white leading-tight" data-id="element-969" data-mp="25mdvpf1zpibv">
                  {getSignalLabel(trait.currentValue)}
                </div>
                {/* Label - Tertiary */}
                <div className="text-xs font-mono text-gray-500 uppercase tracking-widest leading-tight" data-id="element-970" data-mp="3lbmyswbjl2se">
                  {trait.name}
                </div>
              </div>;
        })}
        </div>

        {/* Chart Section */}
        <div className="space-y-4 pt-2" data-id="element-971" data-mp="3v4kih5s9sxsl">
          <div className="flex items-center justify-between" data-id="element-972" data-mp="37c3r24mn7tgm">
            <span className="text-[10px] font-mono text-gray-500 uppercase tracking-widest" data-id="element-973" data-mp="37eq76snlnhag">
              Trend Analysis
            </span>

            {/* Legend */}
            <div className="flex gap-3" data-id="element-974" data-mp="3hcnvth9dfg3l">
              {communicationData.traits.map(trait => <div key={trait.name} className="flex items-center gap-1.5 text-[10px] font-mono text-gray-400 uppercase tracking-widest" data-id="element-975" data-mp="3caf38m0eug20">
                  <span className="w-2 h-2" style={{
                backgroundColor: trait.color
              }} data-id="element-976" data-mp="rl94rl9plal8" />
                  <span className="hidden sm:inline" data-id="element-977" data-mp="13mqykp6ta9dx">{trait.name}</span>
                </div>)}
            </div>
          </div>

          {/* SVG Chart */}
          <div className="relative h-32 w-full" data-id="element-978" data-mp="1dmrm6nbprbnx">
            <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-full overflow-visible" preserveAspectRatio="none" data-id="element-979" data-mp="1dlghmlag1zvr">
              {/* Grid Lines - More Subtle */}
              {[0, 25, 50, 75, 100].map(tick => <line key={tick} x1="0" y1={getY(tick)} x2="100" y2={getY(tick)} stroke="currentColor" strokeOpacity="0.2" strokeWidth="0.2" className="text-gray-800" data-id="element-980" data-mp="pvvmz54xhe6n" />)}

              {/* Data Lines */}
              {communicationData.traits.map((trait, traitIndex) => <g key={trait.name} data-id="element-981" data-mp="2bf7qg9z18735" data-mpi={traitIndex}>
                  <path d={getPath(trait.values)} fill="none" stroke={trait.color} strokeWidth="1.5" strokeLinecap="square" strokeLinejoin="miter" data-id="element-982" data-mp="2bg91wfjymvle" />

                  {/* Interaction Points */}
                  {trait.values.map((val, pointIndex) => <circle key={pointIndex} cx={getX(pointIndex, trait.values.length)} cy={getY(val)} r="2" fill={trait.color} stroke="#0a0a0a" strokeWidth="0.5" className="cursor-pointer hover:r-3 transition-all duration-200" onMouseEnter={e => {
                const rect = e.currentTarget.getBoundingClientRect();
                setHoveredPoint({
                  traitIndex,
                  pointIndex,
                  value: val,
                  x: rect.left + rect.width / 2,
                  y: rect.top
                });
              }} onMouseLeave={() => setHoveredPoint(null)} data-id="element-983" data-mp="1rqynr5zv4ipe" data-mpi={pointIndex} />)}
                </g>)}
            </svg>

            {/* Tooltip */}
            <AnimatePresence data-id="element-984" data-mp="2ir3ykxza08wc">
              {hoveredPoint && <motion.div initial={{
              opacity: 0,
              y: 5
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: 5
            }} className="fixed z-50 px-2 py-1 bg-gray-900 border border-gray-700 text-white text-xs font-mono rounded-none shadow-lg pointer-events-none transform -translate-x-1/2 -translate-y-full mt-[-8px]" style={{
              left: hoveredPoint.x,
              top: hoveredPoint.y
            }} data-id="element-985" data-mp="24zh1wx1f8yd1">
                  <div className="font-bold uppercase tracking-wider leading-tight mb-1" data-id="element-986" data-mp="1lbuowvx3ufbh">
                    {communicationData.traits[hoveredPoint.traitIndex].name}
                  </div>
                  <div className="leading-tight text-gray-400" data-id="element-987" data-mp="1v9qsn2npq7zk">
                    <span className="text-white" data-id="element-988" data-mp="291xact6ikn14">
                      {getSignalLabel(hoveredPoint.value)}
                    </span>
                  </div>
                </motion.div>}
            </AnimatePresence>
          </div>

          {/* X-Axis Labels - Tertiary */}
          <div className="flex justify-between text-[10px] font-mono text-gray-500 uppercase tracking-widest px-1" data-id="element-989" data-mp="2e19zjkkuy66v">
            <span data-id="element-990" data-mp="2qji5eyiil8jo">
              {communicationData.traits[0].values.length === 1 ? 'First scan' : `Last ${communicationData.traits[0].values.length} scans`}
            </span>
            <span data-id="element-991" data-mp="2zkzj6a8g8y57">Latest</span>
          </div>
        </div>

        <div className="space-y-1 border-t border-gray-800 pt-4 text-xs text-gray-500" data-id="element-992" data-mp="2zidb89hncgmg">
          <p data-id="element-993" data-mp="2vh047w2ti7dy">
            These are AI-generated communication pattern estimates, not
            psychological diagnoses.
          </p>
          <p data-id="element-994" data-mp="22uyxik3dmxs4">Based on communication patterns, not intent.</p>
        </div>
      </div>

      {/* PROGRESSIVE DISCLOSURE: Full Change History */}
      {communicationData.recentChanges.length > 0 && <div data-id="element-995" data-mp="2buln88mquf5c">
          <button onClick={() => setIsExpanded(!isExpanded)} className="w-full flex items-center justify-between px-6 py-4 text-sm font-bold text-gray-400 hover:text-white hover:bg-gray-900 transition-colors group uppercase tracking-wider" data-id="element-996" data-mp="2bu2i5si6zl8q">
            <span className="flex items-center gap-2" data-id="element-997" data-mp="1a0ai1ilep8cv">
              <Sparkles className="w-4 h-4 text-[#FF0000] group-hover:text-[#FF0000] transition-colors" data-id="element-998" data-mp="19ypre427q9ka" />
              View Full Behavioral Log
              <span className="text-xs font-mono text-gray-500" data-id="element-999" data-mp="102cyh8ehrzql">
                ({communicationData.recentChanges.length} detections)
              </span>
            </span>
            <motion.div animate={{
          rotate: isExpanded ? 180 : 0
        }} transition={{
          duration: 0.3
        }} data-id="element-1000" data-mp="14yl5fyijcmmo">
              <ChevronDown className="w-4 h-4 text-gray-500 group-hover:text-white transition-colors" data-id="element-1001" data-mp="1iscivhze0qno" />
            </motion.div>
          </button>

          <AnimatePresence data-id="element-1002" data-mp="1nqdy1rtp05tv">
            {isExpanded && <motion.div initial={{
          height: 0,
          opacity: 0
        }} animate={{
          height: 'auto',
          opacity: 1
        }} exit={{
          height: 0,
          opacity: 0
        }} transition={{
          duration: 0.3,
          ease: 'easeInOut'
        }} className="overflow-hidden" data-id="element-1003" data-mp="7d5rrm4vw3u9">
                <div className="px-6 pb-6 pt-2 space-y-3 bg-[#050505]" data-id="element-1004" data-mp="20at5lyemojzu">
                  {communicationData.recentChanges.map((change, i) => <div key={i} className="bg-[#0a0a0a] border border-gray-800 p-4 space-y-2" data-id="element-1005" data-mp="1v8tzwhvf1azq" data-mpi={i}>
                      <div className="flex items-center justify-between" data-id="element-1006" data-mp="1mbb9sz2x06gz">
                        <div className="flex items-center gap-2" data-id="element-1007" data-mp="1hboy4u2qiyaa">
                          {/* Change Title - Secondary */}
                          <span className="text-sm font-bold text-white uppercase tracking-wider leading-tight" data-id="element-1008" data-mp="3gn87vavv4al0">
                            {change.trait}
                          </span>
                          {/* Badge - Semantic Color */}
                          <span className={`
                            inline-flex items-center gap-1 px-2 py-0.5 border text-[10px] font-mono uppercase tracking-widest
                            ${change.direction === 'up' ? 'bg-green-500/10 text-green-500 border-green-500/30' : 'bg-orange-500/10 text-orange-500 border-orange-500/30'}
                          `} data-id="element-1009" data-mp="3j6v2gq8w6kz5">
                            {change.direction === 'up' ? <TrendingUp className="w-3 h-3" data-id="element-1010" data-mp="35b0t6647htli" /> : <TrendingDown className="w-3 h-3" data-id="element-1011" data-mp="3aa3ylro3m1u0" />}
                            {change.direction === 'up' ? 'Stronger signal' : 'Weaker signal'}
                          </span>
                        </div>
                        {/* Metadata - Tertiary */}
                        <span className="text-[10px] font-mono text-gray-500 uppercase tracking-widest" data-id="element-1012" data-mp="s39jjleniwq8">
                          Scan {change.scanRef}
                        </span>
                      </div>
                      {/* Description - Secondary */}
                      <p className="text-sm font-mono text-gray-400 leading-relaxed" data-id="element-1013" data-mp="x1kuy0klhfak">
                        {change.example}
                      </p>
                    </div>)}
                </div>
              </motion.div>}
          </AnimatePresence>
        </div>}
    </div>;
}